import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Alert } from 'reactstrap';
import css from 'styled-jsx/css';

import {
  AREA,
  BAR_ENGINE_UTILIZATION,
  BAR_FUEL_UTILIZATION,
  BOOLEAN,
  CAN_SCAN,
  COLUMN_GRAPH,
  COMMAND,
  GAUGE,
  GAUGE_RANGE,
  HIGH_BAR,
  IMAGE,
  LINE_GRAPH,
  LINE_HIGH_CHART,
  LOCATION,
  MAINTENANCE,
  MAP,
  PIE,
  TEXT,
  TEXT_ENGINE,
  TEXT_FUEL,
  TEXT_STATIC,
  TEXT_TAGS,
  VERTICAL_COLUMN_GRAPH,
} from '../../Constants/WidgetTypes';
import WidgetUtils from '../../Lib/WidgetUtils';

import AssetPathWidget from './AssetPathWidget';
import BooleanWidget from './BooleanWidget';
import CommandsWidget from './CommandsWidget';
import CurrentLocationWidget from './CurrentLocationWidget';
import FlexibleRangeGaugeWidget from './FlexibleRangeGaugeWidget';
import LegacyGaugeWidget from './LegacyGaugeWidget';
import MaintenanceWidget from './MaintenanceWidget';
import StaticTextWidget from './StaticTextWidget';
import TextTagsWidget from './TextTagsWidget';
import TextWidget from './TextWidget';
import {
  EngineUtilizationBarChart,
  FuelUtilizationBarChart,
} from './UtilizationBarWidgets';
import {
  EngineUtilizationTextWidget,
  FuelUtilizationTextWidget,
} from './UtilizationTextWidgets';
import { GraphWidget, ScanWidget } from './WidgetDisplayComponents';
import ImageOverlayWidget from './ImageOverlayWidget';
import { BrandPrimarySpinningCircle } from '../LoadingCircle';

const { className: alertClass, styles: alertStyles } = css.resolve`
  .alert {
    z-index: 1;
    top: 40%;
    left: 0;
    right: 0;
    width: 260px;
    box-shadow: 0px 0px 10px 0px var(--icon-link-gray);
  }
`;

const ALERT_MESSAGE_MAP = new Map([
    [AREA, 'No data to display for the selected time range'],
    [BOOLEAN, 'No signal data available for these signals'],
    [COLUMN_GRAPH, 'No data has been received for these signals'],
    [COMMAND, 'No commands have been defined for this smart tile'],
    [GAUGE, 'No data has been received for this signal'],
    [HIGH_BAR, 'No data has been received for these signals'],
    [LINE_GRAPH, 'No data to display for the selected time range'],
    [LINE_HIGH_CHART, 'No data to display for the selected time range'],
    [LOCATION, 'No data to display for the selected time range'],
    [MAP, 'No data has been received for this signal'],
    [PIE, 'No data has been received for these signals'],
    [CAN_SCAN, 'No scan data available'],
    [TEXT, 'No data has been received for these signals'],
    [TEXT_STATIC, 'No text has been configured for this tile'],
    [TEXT_TAGS, 'No tags have been defined for this smart tile'],
    [VERTICAL_COLUMN_GRAPH, 'No data has been received for these signals'],
  ]),
  flexDisplayTypesSet = new Set([
    BOOLEAN,
    COMMAND,
    MAINTENANCE,
    TEXT,
    TEXT_STATIC,
    TEXT_TAGS,
  ]);
export default function Widget({
  asset,
  fetchedData,
  graphIndex,
  graphWidgetIDs,
  handleCommandExecutedFromWidget,
  historicalUtilizationData,
  renderNextGraph,
  returningToRealTime,
  signalNamesMap,
  signalUnitsMap,
  textTagsWidgetRef,
  updateRealTime,
  utilizationData,
  widget,
  widget: {
    _id: widgetID,
    settings,
    settings: {
      aggregation,
      boolean,
      commands,
      displayTextAsGrid,
      links,
      scan,
      showTimeSinceLastUpdated,
      staticText,
      tags,
      text,
      title,
    } = {},
    type,
  } = {},
}) {
  const widgetUtils = useMemo(() => new WidgetUtils(), []);

  const data = useMemo(() => {
    switch (type) {
      case AREA:
      case LINE_HIGH_CHART:
      case LINE_GRAPH:
      case COLUMN_GRAPH:
      case HIGH_BAR:
      case IMAGE:
      case VERTICAL_COLUMN_GRAPH:
      case PIE: {
        const { last, lines } = widgetUtils.getWidgetAttributes(type, settings);
        const dataMap = widgetUtils.parseFetchedData(fetchedData, lines, last);
        return widgetUtils.parseEventsForGraph(
          lines,
          dataMap,
          type,
          signalNamesMap,
          signalUnitsMap
        );
      }
      case BAR_ENGINE_UTILIZATION:
      case BAR_FUEL_UTILIZATION:
        return historicalUtilizationData;
      case BOOLEAN: {
        const dataMap = widgetUtils.parseFetchedData(
          fetchedData,
          boolean,
          true
        );
        return boolean.map(({ key: name, datapointID, type }) => {
          const { createdAt = '', signals = {} } =
            dataMap.get(datapointID) || {};

          let value;
          let time;
          if (Object.keys(signals || {})?.length) {
            const target =
              Object.values(signals).find(({ name: n }) => n === name) || {};

            value = target.value;
            time = !createdAt
              ? '--'
              : type === 'outputSignal'
                ? moment.utc(createdAt).format('MM-DD-YYYY')
                : moment(createdAt).format('MM-DD-YYYY HH:mm');
          }

          return { name, value, time };
        });
      }
      case COMMAND:
        return commands;
      case LOCATION:
      case MAP:
        return fetchedData.get(widget?.settings?.datapointID);
      case CAN_SCAN: {
        const dataMap = widgetUtils.parseFetchedData(fetchedData, scan, true);
        const [{ datapointID }] = scan || [{}];
        const { signals } = dataMap.get(datapointID) || {};
        const [signal] = Object.values(signals || {});
        return signal?.value ?? '';
      }
      case TEXT: {
        const dataMap = widgetUtils.parseFetchedData(fetchedData, text, true);
        return text.map(({ key: name, datapointID, type }) => {
          const { createdAt = '', signals = [] } =
            dataMap.get(datapointID) || {};
          const { display_precision } =
            signalUnitsMap.get(datapointID)?.[name] ?? {};

          let unit = '';
          let value;
          if (Object.keys(signals || {})?.length) {
            const target =
              Object.values(signals).find(({ name: n }) => n === name) || {};
            if (target?.uom) unit = target.uom;

            switch (typeof target?.value) {
              case 'string':
                value = target.value;
                break;
              case 'number':
                value = _truncateValue(target.value, display_precision);
                break;
              case BOOLEAN:
                value = String(target.value);
                break;
              default:
                value = '';
                break;
            }
          }

          return { name, timestamp: createdAt, type, unit, value };
        });
      }
      case TEXT_ENGINE:
      case TEXT_FUEL:
        return utilizationData;
      case TEXT_STATIC:
        return { links, staticText };
      case TEXT_TAGS:
        return tags;
      default:
        return null;
    }
  }, [
    boolean,
    commands,
    fetchedData,
    historicalUtilizationData,
    links,
    scan,
    settings,
    signalNamesMap,
    signalUnitsMap,
    staticText,
    tags,
    text,
    type,
    utilizationData,
    widget?.settings?.datapointID,
    widgetUtils,
  ]);

  const alertMessage = useMemo(
      () => ALERT_MESSAGE_MAP.get(type) || 'No data available',
      [type]
    ),
    alertVisibility = useMemo(
      () => !returningToRealTime && widgetUtils.noDataIsAvailable(data, type),
      [data, returningToRealTime, type, widgetUtils]
    ),
    flexDisplay = useMemo(
      () => (flexDisplayTypesSet.has(type) ? ' d-flex flex-column' : ''),
      [type]
    ),
    overflowDisplay = useMemo(
      () =>
        widgetID &&
        [BOOLEAN, COMMAND, MAINTENANCE, TEXT, TEXT_STATIC, TEXT_TAGS].includes(
          type
        )
          ? ' pb-2 overflow-auto'
          : '',
      [type, widgetID]
    );

  return (
    <div
      className={`position-absolute h-100 w-100${flexDisplay}${overflowDisplay}`}
    >
      <Alert
        className={`${alertClass} mx-auto position-fixed text-center text-muted`}
        color="light"
        isOpen={alertVisibility}
      >
        {alertMessage}
      </Alert>
      {(() => {
        if (returningToRealTime) {
          return (
            <div className="h-100 w-100 d-flex align-items-center justify-content-center">
              <BrandPrimarySpinningCircle size="3x" />
            </div>
          );
        }

        switch (type) {
          case AREA:
          case LINE_HIGH_CHART:
          case LINE_GRAPH:
          case COLUMN_GRAPH:
          case HIGH_BAR:
          case VERTICAL_COLUMN_GRAPH:
          case PIE:
            return (
              <GraphWidget
                alertVisibility={alertVisibility}
                fetchedData={fetchedData}
                graphIndex={graphIndex}
                graphWidgetIDs={graphWidgetIDs}
                renderNextGraph={renderNextGraph}
                series={data}
                settings={settings}
                signalUnitsMap={signalUnitsMap}
                type={type}
              />
            );
          case BAR_ENGINE_UTILIZATION:
            return (
              <EngineUtilizationBarChart
                aggregation={aggregation}
                data={data}
                graphIndex={graphIndex}
                graphWidgetIDs={graphWidgetIDs}
                renderNextGraph={renderNextGraph}
                title={title}
              />
            );
          case BAR_FUEL_UTILIZATION:
            return (
              <FuelUtilizationBarChart
                aggregation={aggregation}
                data={data}
                graphIndex={graphIndex}
                graphWidgetIDs={graphWidgetIDs}
                renderNextGraph={renderNextGraph}
                title={title}
              />
            );
          case BOOLEAN:
            return <BooleanWidget data={data} />;
          case CAN_SCAN:
            return <ScanWidget data={data} />;
          case COMMAND:
            return (
              <CommandsWidget
                asset={asset}
                commands={commands}
                handleCommandExecuted={handleCommandExecutedFromWidget}
              />
            );
          case GAUGE:
            return (
              <LegacyGaugeWidget
                fetchedData={fetchedData}
                graphIndex={graphIndex}
                graphWidgetIDs={graphWidgetIDs}
                renderNextGraph={renderNextGraph}
                signalUnitsMap={signalUnitsMap}
                widget={widget}
              />
            );
          case GAUGE_RANGE:
            return (
              <FlexibleRangeGaugeWidget
                fetchedData={fetchedData}
                graphIndex={graphIndex}
                graphWidgetIDs={graphWidgetIDs}
                renderNextGraph={renderNextGraph}
                signalUnitsMap={signalUnitsMap}
                widget={widget}
              />
            );
          case IMAGE:
            return (
              <ImageOverlayWidget
                asset={asset}
                data={data}
                fetchedData={fetchedData}
                signalUnitsMap={signalUnitsMap}
                widget={widget}
              />
            );
          case LOCATION:
            return (
              <AssetPathWidget
                assetPathData={data}
                datapointID={widget?.settings?.datapointID}
                fetchedData={fetchedData}
                signalUnitsMap={signalUnitsMap}
                widgetID={widgetID}
              />
            );
          case MAINTENANCE:
            return (
              <MaintenanceWidget
                asset={asset}
                shadow={asset?.shadow}
                signalUnitsMap={signalUnitsMap}
                widget={widget}
              />
            );
          case MAP:
            return (
              <CurrentLocationWidget
                datapointID={widget?.settings?.datapointID}
                fetchedData={fetchedData}
              />
            );
          case TEXT:
            return (
              <TextWidget
                data={data}
                displayTextAsGrid={displayTextAsGrid}
                showTimeSinceLastUpdated={showTimeSinceLastUpdated}
                updateRealTime={updateRealTime}
              />
            );
          case TEXT_ENGINE:
            return <EngineUtilizationTextWidget data={data} />;
          case TEXT_FUEL:
            return <FuelUtilizationTextWidget data={data} />;
          case TEXT_STATIC:
            return <StaticTextWidget data={data} />;
          case TEXT_TAGS:
            return (
              <TextTagsWidget
                asset={asset}
                ref={textTagsWidgetRef}
                widget={widget}
              />
            );
          default:
            return null;
        }
      })()}
      {alertStyles}
    </div>
  );
}

Widget.propTypes = {
  asset: PropTypes.object,
  fetchedData: PropTypes.object,
  graphIndex: PropTypes.number,
  graphWidgetIDs: PropTypes.arrayOf(PropTypes.string),
  handleCommandExecutedFromWidget: PropTypes.func,
  historicalUtilizationData: PropTypes.object,
  renderNextGraph: PropTypes.func,
  returningToRealTime: PropTypes.bool,
  signalNamesMap: PropTypes.object,
  signalUnitsMap: PropTypes.object,
  textTagsWidgetRef: PropTypes.object,
  updateRealTime: PropTypes.bool,
  utilizationData: PropTypes.object,
  widget: PropTypes.object,
};

function _truncateValue(value, precision) {
  return Number.isInteger(value)
    ? value
    : Number(value).toFixed(precision ?? 2);
}
